import { Badge, Button, Callout, Dialog, DialogCancel, Skeleton, useToast } from '@fruuit/ui'
import { LogOut, Monitor } from 'lucide-react'
import { useState } from 'react'
import { errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { revokeSession, sessionsQueryKey, signOutEverywhere, useSessions } from './api'
import { describeDevice, formatDateTime, METHOD_LABELS } from './format'

/** The user's active sessions, with per-session sign-out and sign-out everywhere (P-ID-011). */
export function SessionsList() {
  const me = useSession()
  const sessions = useSessions()
  const { toast } = useToast()
  const [confirmAll, setConfirmAll] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const revoke = useApiMutation((id: string, ctx) => revokeSession(id, ctx), {
    invalidate: [sessionsQueryKey],
    onSuccess: () => toast({ tone: 'success', message: 'That session was signed out.' }),
    onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
  })
  const impersonated = me.impersonated_by !== null

  async function everywhere() {
    setSigningOut(true)
    try {
      await signOutEverywhere()
    } catch (error) {
      setSigningOut(false)
      toast({ tone: 'error', message: errorMessage(error) })
    }
  }

  if (sessions.isPending) {
    return (
      <div role="status" aria-label="Loading sessions" className="flex flex-col gap-3">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }
  if (sessions.isError) {
    return (
      <Callout tone="warning">
        We couldn't load your sessions. {errorMessage(sessions.error)}
      </Callout>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <ul className="divide-y divide-line rounded-lg border border-line">
        {sessions.data.items.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <Monitor aria-hidden="true" className="size-5 shrink-0 text-sage" />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                {describeDevice(s.user_agent)}
                {s.current ? <Badge tone="positive">This device</Badge> : null}
                {s.impersonated ? <Badge tone="watch">Operator view</Badge> : null}
              </p>
              <p className="mt-0.5 text-xs text-sage">
                {METHOD_LABELS[s.method] ?? s.method} · signed in {formatDateTime(s.created_at)} ·
                active {formatDateTime(s.last_active_at)}
              </p>
            </div>
            {!s.current && !impersonated ? (
              <Button
                variant="ghost"
                size="sm"
                loading={revoke.isPending && revoke.variables?.vars === s.id}
                onClick={() => revoke.mutate(s.id)}
              >
                Sign out
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {!impersonated ? (
        <div>
          <Button variant="secondary" icon={<LogOut />} onClick={() => setConfirmAll(true)}>
            Sign out everywhere
          </Button>
        </div>
      ) : null}
      <Dialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        size="sm"
        title="Sign out everywhere?"
        description="Every session ends, including this one. You'll need to sign in again."
        footer={
          <>
            <DialogCancel />
            <Button variant="danger" loading={signingOut} onClick={() => void everywhere()}>
              Sign out everywhere
            </Button>
          </>
        }
      />
    </div>
  )
}
